import i18n from "../i18n";
import { IInboxOrder, IOrderAction } from "../models/messagesInbox/IInboxConversation";
import { orderStage } from "./orderStage";

const tr = (key: string) => i18n.t(key, { ns: "messages" });

const roundPence = (value: number) => Math.round(value * 100) / 100;

/** What the buyer paid in all (item + postage), as the channel reports it when we have that. */
export function orderTotal(o: IInboxOrder): number {
  return roundPence(o.totalPaid ?? o.pricePaid + o.postage);
}

/** What's left to refund: total paid less refunds already made. Never below zero. */
export function remainingRefundable(o: IInboxOrder): number {
  return Math.max(0, roundPence(orderTotal(o) - (o.refundedSoFar ?? 0)));
}

/**
 * Why this action can't be done on the order, or undefined when it can. Live orders carry the
 * API's own canCancel / canRefund and reasons; sample orders go by the dispatch stage.
 */
export function orderActionBlock(o: IInboxOrder, kind: IOrderAction["kind"]): string | undefined {
  if (kind === "cancel") {
    if (o.canCancel === false) return o.cannotCancelReason || tr("This order can't be cancelled.");
    if (o.canCancel === undefined && orderStage(o) === "with_courier") {
      return tr("Already dispatched, so it can't be cancelled. Refund it instead once it's returned, or offer a partial refund.");
    }
    return undefined;
  }
  if (o.canRefund === false) return o.cannotRefundReason || tr("This order can't be refunded in Wilma.");
  if (remainingRefundable(o) <= 0) return tr("Already refunded in full.");
  return undefined;
}

/** A refund amount is fine when it's whole pence, more than nothing and no more than what's left to refund. */
export function refundAmountOk(o: IInboxOrder, amount: number): boolean {
  return Number.isFinite(amount) && amount > 0 && roundPence(amount) === amount && amount <= remainingRefundable(o);
}
